import { getMenuList } from '@/api/serviceDashbord'
import { getShortcutConfig, saveShortcutConfig, resetShortcutConfig } from '@/api/serviceShortcut'
import { normalizeServiceMenus, DEFAULT_SHORTCUT_CODES } from '@/utils/serviceMenus'

// 只保存当前会话，不读取无租户/账号归属的浏览器菜单缓存。
const state = {
  epoch: 0, selectionRevision: 0, menus: [], menuReady: false, menuLoading: false, menuError: '',
  scope: null, configured: false, items: [], version: null,
  configReady: false, configLoading: false, configError: '', configRevision: 0, saving: false, saveError: ''
}
let menuFlight = null
let configFlight = null

function readConfig(res) {
  const config = res && res.resultData
  if (String(res && res.resultCode) !== '200' || !config ||
      !['string', 'number'].includes(typeof config.tenantId) || String(config.tenantId).trim() === '' ||
      !['string', 'number'].includes(typeof config.accountId) || String(config.accountId).trim() === '' ||
      typeof config.configured !== 'boolean' || !['string', 'number'].includes(typeof config.version) || !Array.isArray(config.items) ||
      !config.items.every(item => item && typeof item.menuCode === 'string' && item.menuCode && Number.isInteger(item.sortNo) && item.sortNo >= 0)) {
    throw new Error('Invalid shortcut configuration')
  }
  if (new Set(config.items.map(item => item.menuCode)).size !== config.items.length) throw new Error('Duplicate shortcut configuration')
  if (!config.configured && config.items.length) throw new Error('Default shortcut state must have no custom items')
  return config
}

const mutations = {
  RESET(state) {
    state.epoch++
    state.menus = []
    state.menuReady = false
    state.menuLoading = false
    state.menuError = ''
    state.scope = null
    state.configured = false
    state.items = []
    state.version = null
    state.configReady = false
    state.configLoading = false
    state.configError = ''
    state.configRevision = 0
    state.saving = false
    state.saveError = ''
    menuFlight = null
    configFlight = null
    try { localStorage.removeItem('cachedMenu') } catch (e) { /* 存储不可用时继续清理内存。 */ }
  },
  SET_MENUS(state, menus) { state.menus = menus; state.menuReady = true },
  TOUCH_MENU(state) { state.selectionRevision++ },
  SET_MENU_LOADING(state, value) { state.menuLoading = value },
  SET_MENU_ERROR(state, value) { state.menuError = value },
  SET_CONFIG_LOADING(state, value) { state.configLoading = value },
  SET_CONFIG_ERROR(state, value) { state.configError = value },
  SET_CONFIG(state, config) {
    state.scope = { tenantId: String(config.tenantId), accountId: String(config.accountId) }
    state.configured = config.configured
    state.items = config.items.slice().sort((a, b) => a.sortNo - b.sortNo)
    state.version = config.version
    state.configReady = true
    state.configRevision++
  },
  SET_SAVING(state, value) { state.saving = value },
  SET_SAVE_ERROR(state, value) { state.saveError = value }
}

const actions = {
  // 重新进入服务区域时刷新；区域内切换业务页面不会重建公共布局或重复调用。
  init({ dispatch }) { return Promise.all([dispatch('loadMenus', true), dispatch('loadConfig', true)]) },
  loadMenus({ state, commit }, force = false) {
    if (state.menuReady && !force) return Promise.resolve(true)
    if (menuFlight && menuFlight.epoch === state.epoch) return menuFlight.promise
    const epoch = state.epoch
    commit('SET_MENU_LOADING', true)
    commit('SET_MENU_ERROR', '')
    const promise = (async () => {
      try {
        const res = await getMenuList()
        if (epoch !== state.epoch) return false
        if (String(res && res.resultCode) !== '200' || !Array.isArray(res.resultData)) throw new Error('Invalid menu response')
        commit('SET_MENUS', normalizeServiceMenus(res.resultData))
        return true
      } catch (e) {
        if (epoch === state.epoch) commit('SET_MENU_ERROR', '菜单加载失败，请重新加载。')
        return false
      } finally {
        if (epoch === state.epoch) { commit('SET_MENU_LOADING', false); menuFlight = null }
      }
    })()
    menuFlight = { epoch, promise }
    return promise
  },
  loadConfig({ state, commit }, force = false) {
    if (state.configReady && !force) return Promise.resolve(true)
    if (configFlight && configFlight.epoch === state.epoch) return configFlight.promise
    const epoch = state.epoch
    const revision = state.configRevision
    commit('SET_CONFIG_LOADING', true)
    commit('SET_CONFIG_ERROR', '')
    const promise = (async () => {
      try {
        const res = await getShortcutConfig()
        // 请求发起后若已有更晚的配置写入，不允许旧结果覆盖新排序。
        if (epoch !== state.epoch || revision !== state.configRevision) return false
        const config = readConfig(res)
        if (state.scope && (String(config.tenantId) !== state.scope.tenantId || String(config.accountId) !== state.scope.accountId)) {
          commit('RESET')
          commit('SET_CONFIG_ERROR', '登录身份已变化，请重新加载快捷操作。')
          return false
        }
        commit('SET_CONFIG', config)
        return true
      } catch (e) {
        if (epoch === state.epoch && revision === state.configRevision) commit('SET_CONFIG_ERROR', state.configReady
          ? '快捷设置更新失败，请重新加载后重试。' : '快捷设置加载失败，暂时显示默认入口；请重试。')
        return false
      } finally {
        if (epoch === state.epoch) { commit('SET_CONFIG_LOADING', false); configFlight = null }
      }
    })()
    configFlight = { epoch, promise }
    return promise
  },
  async persist({ state, commit, dispatch }, { codes = [], reset = false }) {
    if (!state.configReady || !state.menuReady || state.saving || state.configLoading || state.menuLoading) return false
    const epoch = state.epoch
    const scope = { ...state.scope }
    commit('SET_SAVING', true)
    commit('SET_SAVE_ERROR', '')
    try {
      const res = reset
        ? await resetShortcutConfig({ version: state.version })
        : await saveShortcutConfig({ version: state.version, items: codes.map((menuCode, sortNo) => ({ menuCode, sortNo })) })
      if (epoch !== state.epoch) return false
      if (String(res && (res.resultCode || res)) === '409') {
        const refreshed = await dispatch('loadConfig', true)
        if (epoch === state.epoch) commit('SET_SAVE_ERROR', refreshed
          ? '设置已在其他设备更新，已重新加载；请确认选择后重试保存。'
          : '设置已在其他设备更新，请重新加载设置后再保存。')
        return false
      }
      const config = readConfig(res)
      if (String(config.tenantId) !== scope.tenantId || String(config.accountId) !== scope.accountId) {
        commit('RESET')
        return false
      }
      if (!reset && !config.configured) throw new Error('Shortcut selection was not persisted')
      if (reset && config.configured) throw new Error('Default shortcuts were not restored')
      const savedCodes = config.items.slice().sort((a, b) => a.sortNo - b.sortNo).map(item => item.menuCode)
      if (!reset && (savedCodes.length !== codes.length || savedCodes.some((code, index) => code !== codes[index]))) {
        commit('SET_SAVE_ERROR', '保存接口返回的菜单顺序与本次设置不一致，尚未确认保存成功；当前预览已保留，请重试或联系管理员。')
        return false
      }
      commit('SET_CONFIG', config)
      return true
    } catch (e) {
      if (epoch === state.epoch) commit('SET_SAVE_ERROR', '快捷设置保存失败，当前选择已保留，请重试。')
      return false
    } finally {
      if (epoch === state.epoch) commit('SET_SAVING', false)
    }
  }
}

export default {
  namespaced: true, state, mutations, actions,
  getters: {
    shortcutCodes: state => state.configured ? state.items.map(item => item.menuCode) : DEFAULT_SHORTCUT_CODES.slice()
  }
}
